<template>
    <q-dialog v-model="showGroupInformation" @hide="closeDialog()">
        <q-card style="min-width: 80vw; min-height: 700px; border-radius: 10px !important;">
            <q-card-section>
                <!-- Group Title -->
                <div class="group-title">
                    <span class="group-name">Group: {{ groupRow.group_name }}</span>
                </div>

                <!-- Group Information -->
                <div class="group-info">
                    <div class="group-info-item">
                        <span class="info-label">Group Type:</span> {{ groupRow.group_type }}
                    </div>
                    <div class="group-info-item">
                        <span class="info-label">Start Date:</span> {{ groupRow.start_date.split('T')[0]}}
                    </div>
                    <div class="group-info-item">
                        <q-tooltip class="text-body2">This is the number of members who have signed up for the group.</q-tooltip>
                        <span class="info-label">Total Members:</span> {{ groupRow.num_members }}
                    </div>
                    <div class="group-info-item">
                        <q-tooltip class="text-body2">This is the number of members who have attended at least one session.</q-tooltip>
                        <span class="info-label">Total Active Members:</span> {{ totalActiveGroupMembers }}
                    </div>
                    <div class="group-info-item">
                        <span class="info-label">Total Sessions:</span> {{ groupRow.num_sessions }}
                    </div>
                    <div class="group-info-item">
                        <span class="info-label">Group Coordinator:</span> {{ groupRow.coordinator }}
                    </div>
                    <div class="group-info-item">
                        <span class="info-label">Overall Attendance:</span> {{ totalAttendancePercent }}
                    </div>
                    <div class="group-info-item">
                        <span class="info-label">Time Taken:</span> {{ additionalInformation.total_time_taken }}
                    </div>
                </div>
            </q-card-section>

            <q-tabs v-model="tab" dense class="tabs-container" active-color="primary" align="justify" narrow-indicator>
                <q-tab no-caps class="client-graph-tab" name="demographic" label="Demographics" />
                <q-tab no-caps class="client-graph-tab" name="members" label="Members" />
                <q-tab no-caps class="client-graph-tab" name="sessions" label="Sessions" />
            </q-tabs>

            <q-tab-panels v-model="tab">
                <q-tab-panel name="demographic">
                    <div class="interaction-graphs">
                        <q-linear-progress v-if="!dataReceived" indeterminate />
                        <div v-if="ethnicityChartData" class="graph-chart small-graph">
                            <Pie v-bind:chartData="this.ethnicityChartData" v-bind:chartOptions="ethnicityChartOptions" />
                        </div>
                        <div v-if="ageRangeChartData" class="graph-chart small-graph">
                            <Pie v-bind:chartData="this.ageRangeChartData" v-bind:chartOptions="ageRangeChartOptions" />
                        </div>

                        <div v-if="genderChartData" class="graph-chart small-graph">
                            <Pie v-bind:chartData="this.genderChartData" v-bind:chartOptions="genderChartOptions" />
                        </div>
                    </div>
                </q-tab-panel>
                <q-tab-panel name="members">
                    <div v-if="showGroupMembers">
                        <TableFilterComponent :tableInformation="members" :filterKeys="['Gender', 'Ethnicity', 'Age']"
                            @updateFilters="updateFilters" />

                        <q-table class="q-pa-md" :style="{ height: '800px' }" :rows="filteredRows" :columns="columns"
                            :rows-per-page-options="[15]">
                            <template v-slot:top-right>
                                <q-btn no-caps fab-mini flat icon="download"
                                    @click="exportTable(filteredRows, `${groupRow.group_name} - Members.csv`, { columns })" />
                            </template>
                        </q-table>
                    </div>
                </q-tab-panel>
                <q-tab-panel name="sessions">
                   <q-table class="q-pa-md" :style="{ height: '800px' }" :rows="sessions" :columns="sessionColumns"
                        :rows-per-page-options="[15]" :loading=loadingSessions>
                        <template v-slot:top-left v-if="['odyssey', 'mana_ake'].includes(groupRow.source)">
                            Note: The <strong>Total Members</strong> value includes all clients within that group. 
                            However, as a result of a data retrieval process, the <strong>Attendees</strong> and <strong>Total Active Members</strong> will only reference clients that are associated with Purapura Whetu staff members. 
                        </template>
                        <template v-slot:top-right>
                            <q-btn no-caps fab-mini flat icon="download"
                                @click="exportTable(sessions, `${groupRow.group_name} - Sessions.csv`, { columns: sessionColumns })" />
                        </template>
                    </q-table>
                </q-tab-panel>
            </q-tab-panels>

        </q-card>
    </q-dialog>
</template>

<script>

import api from "../../../services/api/api"
import notify from '@/services/util/notify';

import Pie from "../../ChartComponents/Pie.vue";
import { retPieOptions } from "../../DataComponents/chartOptions"

import { ref } from 'vue'

import TableFilterComponent from '../TableFilterComponent.vue';
import { exportTable } from "../../../services/util/file";

export default {
    name: "GroupInformationComponent",

    components: { Pie, TableFilterComponent },

    props: {
        groupRow: {
            type: Object,
            required: true
        }
    },


    setup() {
        return {
            tab: ref('demographic'),
        }
    },


    data() {
        return {
            exportTable: exportTable,
            showDescriptions: false,

            showGroupInformation: false,
            dataReceived: false,

            ethnicityChartData: null,
            ethnicityChartOptions: retPieOptions("Ethnicity", "# of People"),

            genderChartData: null,
            genderChartOptions: retPieOptions("Gender", "# of People"),

            ageRangeChartData: null,
            ageRangeChartOptions: retPieOptions("Age Ranges", "# of People"),


            showGroupMembers: false,
            members: [],
            columns: [],

            selectedFilters: {},
            ageFilters: {},

            additionalInformation: {},

            totalActiveGroupMembers: null,

            sessions: [],
            sessionColumns: [
                { name: 'datetime', style: "width: 20%", align: 'left', label: 'Date and Time', field: 'datetime', format: (val) => (new Date(val).toLocaleString('en-NZ', { timeZone: 'UTC' })), sortable: true, sort: (a, b) => new Date(b) - new Date(a),  defaultVisibility: true },
                { name: 'facilitators', style: "width: 20%", align: 'left', label: 'Session Facilitators', field: 'facilitators', sortable: true, defaultVisibility: true },
                { name: 'location', style: "width: 20%", align: 'left', label: 'Location', field: 'location', sortable: true, defaultVisibility: true },
                { name: 'num_attendees', style: "width: 20%", align: 'left', label: 'Attendees', field: 'num_attendees', sortable: true, defaultVisibility: true },
                { name: 'attendance', style: "width: 20%", align: 'left', label: 'Attendance', field: "attendance", format: (val) => `${val.toFixed(0)}%`, sortable: true, defaultVisibility: true },
            ],
            loadingSessions: false,
        }
    },

    async created() {
        this.retrieveGroupMembers(this.groupRow);

        await this.retrieveGroupInfo(this.groupRow);
        await this.retrieveAdditionalGroupInformation(this.groupRow);
        await this.retrieveActiveGroupMemberCount(this.groupRow.id);
        await this.retrieveGroupSessions(this.groupRow.id);
    },


    computed: {
        /**
         * Generate filtered rows by applying selected filters to staff data
         */
        filteredRows() {
            if (this.members.length == 0) {
                return this.members;
            }

            let filteredMembers = this.members;

            if (Object.keys(this.ageFilters).length > 0) {
                if (this.ageFilters.type) {
                    filteredMembers = this.members.filter((row) => row.age >= this.ageFilters.age)
                } else {
                    filteredMembers = this.members.filter((row) => row.age < this.ageFilters.age)
                }
            }

            return filteredMembers.filter((row) => {
                return Object.keys(this.selectedFilters).every((key) => {

                    if (this.selectedFilters[key].length === 0) {
                        return true;
                    }
                    return this.selectedFilters[key].includes(row[key]);
                });
            });
        },


        totalAttendancePercent() {
            if ( this.additionalInformation.total_attendees ) {
                if (this.totalActiveGroupMembers === 0) return '0%';
                const percent = (this.additionalInformation.total_attendees / (this.totalActiveGroupMembers * this.groupRow.num_sessions)) * 100;
                return `${percent.toFixed(2)}%`;
            }
            return '0%';
        }
    },


    methods: {
        async retrieveActiveGroupMemberCount(id) {
            try {
                this.totalActiveGroupMembers = (await api.groups.getActiveGroupMemberCount(id)).data;
            } catch (error) {
                this.closeDialog();
                notify.error(error.message, 'top');
            }
        },

        /**
         * @desc Retrieve group aggregation graphs
         * @param {*} row 
         */
        async retrieveGroupInfo(row) {
            this.showGroupInformation = true;
            this.dataReceived = false;

            api.groups.getGroupInformationById(row.id).then((res) => {
                this.ethnicityChartData = res.data.ethnicityData;
                this.genderChartData = res.data.genderData;
                this.ageRangeChartData = res.data.ageRangeData;
                this.showGroupInformation = true;
                this.dataReceived = true;
            }).catch((error) => {
                this.closeDialog();
                notify.error(error.message, 'top');
            })
        },


        /**
         * @desc 
         * @param {} row 
         */
        async retrieveAdditionalGroupInformation(row) {
            api.groups.getAdditionalGroupInformationById(row.id).then((res) => {
                this.additionalInformation = res.data[0];
            }).catch((error) => {
                this.closeDialog();
                notify.error(error.message, 'top');
            })
        },


        /**
         * @desc Retrieve table of group members
         * @param {*} row 
         */
        async retrieveGroupMembers(row) {
            api.groups.getGroupMembersByGroupId(row.id).then((res) => {
                this.members = res.data.tableRows;
                this.columns = res.data.tableColumns;
                this.showGroupMembers = true;
            }).catch((error) => {
                this.closeDialog();
                notify.error(error.message, 'top');
            })
        },

        async retrieveGroupSessions(id) {
            this.loadingSessions = true;

            try {
                this.sessions = (await api.groups.getGroupSessions(id)).data;
                this.sessions.map((session) => {
                    const attendees = parseInt(session.num_attendees);
                    if (attendees === 0) {
                        session.attendance = 0;
                    } else {
                        session.attendance = (parseInt(session.num_attendees) / this.totalActiveGroupMembers).toFixed(2) * 100;
                    }
                    return session;
                })
            } catch (error) {
                this.closeDialog();
                notify.error(error.message, 'top');
            } finally {
                this.loadingSessions = false;
            }
        },

        /**
         * Resets the cards appearance and default values.
         */
        closeDialog() {
            this.$emit('close')
        },


        updateFilters(filters, ageFilters) {
            this.selectedFilters = filters;
            this.ageFilters = ageFilters;
        },
    }
}

</script>

<style>@import '../../../assets/styles/clientGroup.css';</style>